import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AnimatedBackground } from './components/fx/AnimatedBackground';
import { CustomCursor } from './components/fx/CustomCursor';
import { SmoothScroll } from './components/fx/SmoothScroll';
import { Preloader } from './components/Preloader';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { shouldReduceExperience } from './lib/performance';
const Home = lazy(() => import('./pages/Home').then(module => ({
  default: module.Home
})));
const About = lazy(() => import('./pages/About').then(module => ({
  default: module.About
})));
const Services = lazy(() => import('./pages/Services').then(module => ({
  default: module.Services
})));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail').then(module => ({
  default: module.ServiceDetail
})));
const Projects = lazy(() => import('./pages/Projects').then(module => ({
  default: module.Projects
})));
const Emergency = lazy(() => import('./pages/Emergency').then(module => ({
  default: module.Emergency
})));
const Insurance = lazy(() => import('./pages/Insurance').then(module => ({
  default: module.Insurance
})));
const ServiceAreas = lazy(() => import('./pages/ServiceAreas').then(module => ({
  default: module.ServiceAreas
})));
const Reviews = lazy(() => import('./pages/Reviews').then(module => ({
  default: module.Reviews
})));
const Blog = lazy(() => import('./pages/Blog').then(module => ({
  default: module.Blog
})));
const FAQ = lazy(() => import('./pages/FAQ').then(module => ({
  default: module.FAQ
})));
const Contact = lazy(() => import('./pages/Contact').then(module => ({
  default: module.Contact
})));
function RouteFallback() {
  return <div className="flex min-h-[58vh] items-center justify-center" role="status" aria-label="Loading page" data-id="element-0" data-mp="36huv2f1ajz4">
      <span className="h-9 w-9 rounded-full border-2 border-royal/20 border-t-royal animate-spin" data-id="element-1" data-mp="3udkr489qbgg7" />
    </div>;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="851mnjgb6pze">
      <motion.main key={location.pathname} initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -12
    }} transition={{
      duration: 0.32,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-3" data-mp="864atjm8yebm">
        <Suspense fallback={<RouteFallback data-id="element-5" data-mp="3ljg1tta8ud2s" />} data-id="element-4" data-mp="3ljxw4kdcih82">
          <Routes location={location} data-id="element-6" data-mp="3lj6aa19jvxuz">
            <Route path="/" element={<Home data-id="element-8" data-mp="2xu3mscs8jask" />} data-id="element-7" data-mp="2xun2dmuexn3f" />
            <Route path="/about" element={<About data-id="element-10" data-mp="2xsk289zjqdkz" />} data-id="element-9" data-mp="2xtby7lx22dbs" />
            <Route path="/services" element={<Services data-id="element-12" data-mp="2sv0orcaoa4u3" />} data-id="element-11" data-mp="2sw243syq1dhm" />
            <Route path="/services/:slug" element={<ServiceDetail data-id="element-14" data-mp="18d9ds8p0zot8" />} data-id="element-13" data-mp="2stpmjgwvl553" />
            <Route path="/projects" element={<Projects data-id="element-16" data-mp="18bok0lajc9wf" />} data-id="element-15" data-mp="18cznsl3zdhnj" />
            <Route path="/emergency" element={<Emergency data-id="element-18" data-mp="1d59kcjzlpfys" />} data-id="element-17" data-mp="1d47ydkh1r92a" />
            <Route path="/insurance" element={<Insurance data-id="element-20" data-mp="yfvbdczxt0jx" />} data-id="element-19" data-mp="1d6knqip4wlpd" />
            <Route path="/service-areas" element={<ServiceAreas data-id="element-22" data-mp="ydiiw5wjz7sm" />} data-id="element-21" data-mp="ygmsjissevqo" />
            <Route path="/reviews" element={<Reviews data-id="element-24" data-mp="13fpzj8sbvc1a" />} data-id="element-23" data-mp="13eew5a2so6ap" />
            <Route path="/blog" element={<Blog data-id="element-26" data-mp="1r497fn7gtmsu" />} data-id="element-25" data-mp="13ey2rumzv89f" />
            <Route path="/faq" element={<FAQ data-id="element-28" data-mp="1r5kb7n0wuujy" />} data-id="element-27" data-mp="1r3r11xr39s77" />
            <Route path="/contact" element={<Contact data-id="element-30" data-mp="1w33qmq9chddh" />} data-id="element-29" data-mp="1r2698fw5snd1" />
            <Route path="*" element={<Home data-id="element-32" data-mp="1w2u091ke1479" />} data-id="element-31" data-mp="1w2aqw98szo4u" />
          </Routes>
        </Suspense>
      </motion.main>
    </AnimatePresence>;
}
function hasSeenPreloader() {
  try {
    return sessionStorage.getItem('dream-shield-preloader-seen') === 'true';
  } catch {
    return false;
  }
}
export function App() {
  const [loading, setLoading] = useState(() => !hasSeenPreloader());
  useEffect(() => {
    const updateExperienceClass = () => {
      document.documentElement.classList.toggle('reduced-experience', shouldReduceExperience());
    };
    updateExperienceClass();
    window.addEventListener('resize', updateExperienceClass, {
      passive: true
    });
    return () => window.removeEventListener('resize', updateExperienceClass);
  }, []);
  useEffect(() => {
    document.body.style.overflow = loading ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [loading]);
  return <BrowserRouter data-id="element-33" data-mp="19v88un6cf3d1">
      <AnimatedBackground data-id="element-34" data-mp="14wx24lbch8z1" />
      <CustomCursor data-id="element-35" data-mp="14wna6s6qorqp" />
      {loading && <Preloader onDone={() => setLoading(false)} data-id="element-36" data-mp="14xg771fwc2vn" />}
      <SmoothScroll data-id="element-37" data-mp="zxk73dabkvld">
        <Navbar data-id="element-38" data-mp="zwsk2qusg66p" />
        <AnimatedRoutes data-id="element-39" data-mp="zw8zt3ho15pi" />
        <Footer data-id="element-40" data-mp="zzws5zbdjm2n" />
      </SmoothScroll>
    </BrowserRouter>;
}